import React, {useEffect, useRef, useState, useCallback} from 'react';
import { fabric } from 'fabric';
import 'fabric-eraser-brush';
import { useNavigate } from "react-router";
import { useAuth } from "../hooks/Auth";
import CreatePost from "../components/CreatePost";

/* These constants will set the canvas size and work to keep the container width
proportional to the scaled canvas width, plus some padding for borders and spacing. 
This ensures the layout remains consistent regardless of the canvas size. */
const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1200;
const DISPLAY_SCALE = .5;
const CONTAINER_WIDTH = CANVAS_WIDTH * DISPLAY_SCALE + 40;

const MainPage = () => {
    const canvasRef = useRef(null); // stores DOM element
    const fabricCanvasRef = useRef(null); // stores Fabric instance to be able to use Fabric.js functions
    const cropRectRef = useRef(null); // stores the crop rectangle object when in crop mode to be able to manipulate it and apply cropping later
    const imageUploadRef = useRef(null); // stores the file input element for uploading images to be able to clear its value when needed so that the same image can be re-uploaded if desired
    const imageImportRef = useRef(null); // stores the file input element for importing images to be able to clear its value when needed so that the same image can be re-imported if desired

    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isErasingMode, setIsErasingMode] = useState(false);
    const [drawingBrush, setDrawingBrush] = useState('Pencil');
    const [drawingColor, setDrawingColor] = useState('#000000');
    const [drawingWidth, setDrawingWidth] = useState(5);
    const [shadowColor, setShadowColor] = useState('#000000');
    const [shadowBlur, setShadowBlur] = useState(0);
    const [shadowOffset, setShadowOffset] = useState(0);
    const [erasingWidth, setErasingWidth] = useState(5);
    const [isCropMode, setIsCropMode] = useState(false);
    const [textColor, setTextColor] = useState('#000000');
    const [fontFamily, setFontFamily] = useState('Times New Roman');
    const [fontSize, setFontSize] = useState(20);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [showCreatePost, setShowCreatePost] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    
    const navigate = useNavigate();
    const { user, token } = useAuth();

    // Create Fabric canvas on component mount
    useEffect(() => {
        fabricCanvasRef.current = new fabric.Canvas(canvasRef.current, {
            width: CANVAS_WIDTH,
            height: CANVAS_HEIGHT,
            backgroundColor: '#ffffff',
            erasable: true,
        });

        const canvas = fabricCanvasRef.current;

         // Prevent Fabric's hidden textarea from scrolling the page when editing text
        canvas.on('text:editing:entered', () => {
            const active = canvas.getActiveObject();
            if (active && active.hiddenTextarea) {
                active.hiddenTextarea.style.position = 'fixed';
                // active.hiddenTextarea.style.top = '0';
                // active.hiddenTextarea.style.left = '0';
                active.hiddenTextarea.style.opacity = '0';
            }
        });
 
        // Remove objects that have been fully erased
        canvas.on('erasing:end', ({ targets }) => {
            targets.forEach((obj) => {
                // Check if the object's eraser path covers it entirely by
                // testing if it has no visible pixels — simplest reliable check
                // is whether the object has an eraser clipPath that matches its bounds
                const eraserGroup = obj.eraser;
                if (!eraserGroup) return;
 
                // Render the object offscreen and sample pixels to check if fully erased
                const offscreen = document.createElement('canvas');
                offscreen.width = Math.ceil(obj.getBoundingRect().width);
                offscreen.height = Math.ceil(obj.getBoundingRect().height);
                const ctx = offscreen.getContext('2d');
                obj.cloneAsImage((img) => {
                    img.render(ctx);
                    const pixels = ctx.getImageData(0, 0, offscreen.width, offscreen.height).data;
                    const hasVisiblePixel = pixels.some((val, i) => i % 4 === 3 && val > 0);
                    if (!hasVisiblePixel) {
                        canvas.remove(obj);
                        canvas.renderAll();
                    }
                });
            });
        });

        const isEditingText = () => {
            const obj = canvas.getActiveObject();
            return obj && obj.isEditing;
        }
                
        const handleDeleteKey = (e) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && !isEditingText()) {
                const activeObjects = canvas.getActiveObject();
                if(!activeObjects) return;
                if(activeObjects.type === 'activeSelection') {
                    activeObjects.getObjects().forEach((obj) => {
                        canvas.remove(obj);
                    });
                    canvas.discardActiveObject();
                } else {
                    canvas.remove(activeObjects);
                }
                canvas.renderAll();
            }
        };
       

        window.addEventListener('keydown', handleDeleteKey);

        // Cleanup on component unmount
        return () => {
            canvas.off('erasing:end');
            canvas.off('text:editing:entered');
            window.removeEventListener('keydown', handleDeleteKey);
            canvas.dispose();
        };
    }, []);

    const buildBrushType = (type) => {
        switch(type) {
            // case 'Pencil':
            //     return new fabric.PencilBrush(fabricCanvasRef.current);
            case 'Circle':
                return new fabric.CircleBrush(fabricCanvasRef.current);
            case 'Spray':
                return new fabric.SprayBrush(fabricCanvasRef.current);
            case 'Pencil':
            default:
                return new fabric.PencilBrush(fabricCanvasRef.current);
        }
    };

    const applyBrushType = useCallback(() => {
        if(!fabricCanvasRef.current) return;
        const brush = buildBrushType(drawingBrush);
        brush.color = drawingColor;
        brush.width = drawingWidth;
        brush.shadow = new fabric.Shadow({
            color: shadowColor,
            blur: shadowBlur,
            offsetX: shadowOffset,
            offsetY: shadowOffset,
            affectStroke: true,
        });
        fabricCanvasRef.current.freeDrawingBrush = brush;
    }, [drawingBrush, drawingColor, drawingWidth, shadowColor, shadowBlur, shadowOffset]);

    // Handles drawing functions; updating when states change
    useEffect(() => {
        if(fabricCanvasRef.current) {
            fabricCanvasRef.current.isDrawingMode = isDrawingMode || isErasingMode; // Enable drawing mode if either drawing or erasing is active
            if(isDrawingMode) {
                applyBrushType(drawingBrush, drawingColor, drawingWidth, shadowColor, shadowBlur, shadowOffset);
            } else if(isErasingMode) {
                fabricCanvasRef.current.freeDrawingBrush = new fabric.EraserBrush(fabricCanvasRef.current);
                fabricCanvasRef.current.freeDrawingBrush.width = erasingWidth;
            }
        }
    }, [isDrawingMode, applyBrushType,drawingBrush, drawingColor, drawingWidth, shadowColor, shadowBlur, shadowOffset, isErasingMode, erasingWidth]);


    // Gets image from canvas
    const getCanvasImageFile = async () => {
        const canvas = fabricCanvasRef.current; // gets current canvas

        // get image
        const dataURL = canvas.toDataURL({
            format: "png",
            quality: 1,
        });

        // convert to Blob
        const res = await fetch(dataURL);
        const blob = await res.blob();

        // convert to File and return
        return new File([blob], "canvas-image.png", {
            type: "image/png",
        });
        };


    // Handles image uploading onto canvas
    const handleImageUpload = (e) => {
        const file = e.target.files[0]; // Gets the first file selected by the user
        if (!file) return; // Exit early if no file was selected

        const reader = new FileReader(); // Creates a FileReader to read the file's contents

        reader.onload = (e) => {
            const imageUrl = e.target.result // Extract the base64 data URL of the file

            // Create a Fabric image object from the data URL
            fabric.Image.fromURL(imageUrl, (img) => {

                fabricCanvasRef.current.setBackgroundImage(img, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current), {
                    scaleX: fabricCanvasRef.current.width / img.width,
                    scaleY: fabricCanvasRef.current.height / img.height,
                    erasable: false // Make the background image non-erasable
                });
            }, /*{ crossOrigin: 'anonymous'} - handles loading images from external URLs if needed*/);
        };

        reader.readAsDataURL(file); // Trigger the read, which will fire reader.onload with the result
    };

    // Handles image importing onto canvas
    const handleImageImport = (e) => {
        const file = e.target.files[0]; // Gets the first file selected by the user
        if (!file) return; // Exit early if no file was selected

        const reader = new FileReader(); // Creates a FileReader to read the file's contents

        reader.onload = (e) => {
            const imageUrl = e.target.result // Extract the base64 data URL of the file

            // Create a Fabric image object from the data URL
            fabric.Image.fromURL(imageUrl, (img) => {
                const scale = Math.min(
                    fabricCanvasRef.current.width / img.width,   // Scale needed to fit canvas width
                    fabricCanvasRef.current.height / img.height, // Scale needed to fit canvas height
                    1  // Cap at 1 so small images aren't upscaled
                );
            
                img.scale(scale);

                // Position and style the image on the canvas
                img.set({
                    left: fabricCanvasRef.current.width / 2,  // Center horizontally
                    top: fabricCanvasRef.current.height / 2,  // Center vertically
                    originX: 'center',
                    originY: 'center',
                    cornerColor: 'black',
                    cornerSize: 8,
                    transparentCorners: false,
                    erasable: true, // Make the image erasable when using the eraser tool
                });

                fabricCanvasRef.current.add(img);              // Add the new image to the canvas
                fabricCanvasRef.current.setActiveObject(img);  // Select the image immediately
                fabricCanvasRef.current.renderAll();           // Re-render the canvas to reflect changes
                if (imageImportRef.current) imageImportRef.current.value = ''; // Clear file input values to allow re-uploading the same image if desired
            }, /*{ crossOrigin: 'anonymous'} - handles loading images from external URLs if needed*/);
        };

        reader.readAsDataURL(file); // Trigger the read, which will fire reader.onload with the result
    };

    // Brings up the crop box for user to select what to crop
    const startCrop = () => {
        const canvas = fabricCanvasRef.current;

        setIsCropMode(true);
        canvas.isDrawingMode = false;

        // Create a visible crop rectangle
        const cropRect = new fabric.Rect({
            left: 100,
            top: 100,
            width: 200,
            height: 150,
            fill: 'rgba(0, 0, 0, 0.2)',
            stroke: 'black',
            strokeWidth: 2,
            strokeDashArray: [5, 5],
            transparentCorners: false,
            cornerColor: 'black',
            cornerSize: 8,
            name: 'cropRect'
        });

        cropRectRef.current = cropRect;
        canvas.add(cropRect);
        canvas.setActiveObject(cropRect);
        canvas.renderAll();
    };

    // Function to apply crop
    const applyCrop = () => {
        const canvas = fabricCanvasRef.current;
        const cropRect = cropRectRef.current;
        if (!cropRect) return;

        canvas.remove(cropRect);
        canvas.renderAll();

        // Handles cropped area as new image
        const croppedDataURL = canvas.toDataURL({
            left: cropRect.left,
            top: cropRect.top,
            width: cropRect.getScaledWidth(),
            height: cropRect.getScaledHeight(),
            format: 'png',
            quality: 1
        });

        // Gets the new image's (cropped image) data to set it on the canvas
        fabric.Image.fromURL(croppedDataURL, (newImg) => {
            const scale = Math.min(
                canvas.width / newImg.width,   // Scale needed to fit canvas width
                canvas.height / newImg.height, // Scale needed to fit canvas height
                1  // Cap at 1 so small images aren't upscaled
            );
        
            newImg.scale(scale);

            newImg.set({
                left: canvas.width / 2,
                top: canvas.height / 2,
                originX: 'center',
                originY: 'center',
                cornerColor: 'black',
                cornerSize: 8,
                transparentCorners: false,
            });

            // Clean up, remove original image for cropped image and remove cropRect (crop box)
            canvas.add(newImg);
            canvas.setActiveObject(newImg);
            cropRectRef.current = null;
            setIsCropMode(false);
            canvas.renderAll();
        });
    };

    // Function to cancel crop
    const cancelCrop = () => {
        const canvas = fabricCanvasRef.current;
        if (cropRectRef.current) {
            canvas.remove(cropRectRef.current);
            cropRectRef.current = null;
        }
        setIsCropMode(false);
        canvas.renderAll();
    };

    const addText = () => {
        setIsDrawingMode(false);
        setIsErasingMode(false);
        fabricCanvasRef.current.isDrawingMode = false;

        const text = new fabric.Textbox('Enter text here', {
            left: fabricCanvasRef.current.width / 2,
            top: fabricCanvasRef.current.height / 2,
            originX: 'center',
            originY: 'center',
            width : 400,
            fontFamily: fontFamily,
            fontSize: fontSize,
            fontWeight: isBold ? 'bold' : 'normal',
            fill: textColor,
            fontStyle: isItalic ? 'italic' : 'normal',
            underline: isUnderline,

        });
        fabricCanvasRef.current.add(text);
        fabricCanvasRef.current.setActiveObject(text);
        text.enterEditing();
        text.selectAll();
        fabricCanvasRef.current.renderAll();
    }

    // Handles downloading current canvas content as an image file
    const downloadImage = () => {
        // Export the canvas as a base64-encoded data URL
        const dataURL = fabricCanvasRef.current.toDataURL({
            format: 'png',
            // multiplier: 1, - scale factor
            quality: 1
        });

        const link = document.createElement('a'); // Create a temporary anchor element to trigger the file download
        link.download = 'edited-image.png';  // Set the default filename for the download
        link.href = dataURL;                 // Attach the image data as the link destination
        link.click();                        // Programmatically click the link to start the download
    }

    // Clear entire canvas
    const clearCanvas = () => {
        if(window.confirm('Are you sure you want to clear everything?')) {
            fabricCanvasRef.current.clear();
            fabricCanvasRef.current.backgroundColor = '#ffffff';
            fabricCanvasRef.current.renderAll();
            // Clear file input values to allow re-uploading the same image if desired
            if (imageUploadRef.current) {
                imageUploadRef.current.value = '';
            }
            if (imageImportRef.current) {
                imageImportRef.current.value = '';
            }
        }
    };

    return (
        <div style={styles.container}>
            {showCreatePost &&(
                <CreatePost
                    onClose={() => setShowCreatePost(false)}
                    token={token}

                    initialData={{
                        image: selectedImage
                    }}
                     lockedFields={{
                        image: true, // disables image input
                    }}  
                />
                )}
                   
        
            <div style={styles.header}>
                <h1>PirateStudio</h1>
                <div style={{flexGrow: 1}} /> {/* Spacer to push navigation buttons to the right */}
                    <button style={styles.fileBtn}
                        onClick={() => navigate("/gallery")}
                    > Gallery
                    </button>

                    <button style={styles.fileBtn}
                        onClick={() => navigate(`/profile`, { state: { user: user?.id } })}
                    > Profile
                    </button>

                    <button style={styles.fileBtn}
                    onClick={async () => {
                        const file = await getCanvasImageFile();
                        setSelectedImage(file);
                        setShowCreatePost(true);
                    }}
                    > Create Post
                    </button>

                    <button style={styles.fileBtn}
                        onClick={() => navigate("/login")}
                    > Logout
                    </button>
            </div>

            <div style={styles.controlsBar}>
                <label style={styles.fileBtn}>Upload
                    <input
                        type="file"
                        ref={imageUploadRef}
                        accept="image/*"
                        onChange={handleImageUpload}
                        style= {{display: "none"}}
                    />
                </label>

                <label style={styles.fileBtn}>Import
                    <input
                        type="file"
                        ref={imageImportRef}
                        accept="image/*"
                        onChange={handleImageImport}
                        style= {{display: "none"}}
                    />
                </label>

                <button style={styles.fileBtn}
                    onClick={downloadImage}
                > Download
                </button>
            </div>      

            <div style={styles.canvasWrapper}>
                <div style={{transform: `scale(${DISPLAY_SCALE})`}}>
                    <canvas ref={canvasRef} id="canvas" />
                </div>
            </div>

            {/* Drawing tools */}
            <div style={styles.toolBar}>
                {/* Draw button to trigger drawing mode */}
                <button style={styles.drawBtn}
                    onClick={() => {
                        setIsDrawingMode(!isDrawingMode)
                        setIsErasingMode(false);}}
                > {isDrawingMode ? 'Stop' : 'Draw'}
                </button>
                {/* Brush Controls */}
                {isDrawingMode && (
                    <div style={styles.drawOptions}>
                        {/* Brush selection */}
                        <select value={drawingBrush} onChange={(e) => setDrawingBrush(e.target.value)} style={styles.select}>
                            <option value="Pencil">Pencil</option>
                            <option value="Circle">Circle</option>
                            <option value="Spray">Spray</option>
                        </select>
                        {/* Color selection */}
                        <label style={styles.label}>
                            Color:
                        <input 
                            type="color"
                            value={drawingColor}
                            onChange={(e) => setDrawingColor(e.target.value)}
                            style={styles.labelInput}
                        />
                        </label>
                        {/* Brush width selection */}
                        <label style={styles.label}>
                            Width:
                        <input
                            type="range"
                            min={1}
                            max={100}
                            value={drawingWidth}
                            onChange={(e) => setDrawingWidth(parseInt(e.target.value))}
                            style={styles.labelInput}
                        />
                        </label>
                        {/* Shadow controls */}
                        <label style={styles.label}>
                            Shadow Color:
                        <input
                            type="color"
                            value={shadowColor}
                            onChange={(e) => setShadowColor(e.target.value)}
                            style={styles.labelInput}
                        />
                        </label>    
                        <label style={styles.label}>
                            Shadow Blur:
                        <input
                            type="range"
                            min={0}
                            max={50}
                            value={shadowBlur}
                            onChange={(e) => setShadowBlur(parseInt(e.target.value))}
                            style={styles.labelInput}
                        />
                        </label>    
                        <label style={styles.label}>
                            Shadow Offset:
                        <input
                            type="range"
                            min={0}
                            max={50}
                            value={shadowOffset}
                            onChange={(e) => setShadowOffset(parseInt(e.target.value))}
                            style={styles.labelInput}
                        />
                        </label>    
                    </div>
                )}
                
                {/* Erase button to trigger erasing mode */}
                <button style={styles.drawBtn}
                    onClick={() => {
                        setIsErasingMode(!isErasingMode);
                        setIsDrawingMode(false);
                    }}
                > {isErasingMode ? 'Stop' : 'Erase'}
                </button>
                {/* Eraser width selection */}
                {isErasingMode && (
                    <div style={styles.drawOptions}>
                        <label style={styles.label}>
                            Width:
                        <input
                            type="range"
                            min={1}
                            max={100}
                            value={erasingWidth}
                            onChange={(e) => setErasingWidth(parseInt(e.target.value))}
                            style={styles.labelInput}
                        />
                        </label>
                    </div>
                )}
                
                <div style={{flexGrow: 1}} /> {/* Spacer to push crop and clear buttons to the right */}
                {!isCropMode ? (
                <button 
                    onClick={() => {
                        setIsDrawingMode(false);
                        setIsErasingMode(false);
                        startCrop();
                    }}
                    style={styles.drawBtn}
                >Crop
                </button>
                ) : (
                <>
                    <button onClick={applyCrop} style={{ backgroundColor: 'green', color: 'white' }}>
                        Apply Crop
                    </button>
                    <button onClick={cancelCrop} style={{ backgroundColor: 'red', color: 'white' }}>
                        Cancel
                    </button>
                </>
                )}
                <button
                    onClick={clearCanvas}
                    style={styles.drawBtn}
                > Clear
                </button>
            </div>  

            {/* Text options */}
            <div style={styles.toolBar}>
                <div style={styles.textOptions}>
                    {/* Font family selection */}
                    <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} style={styles.select}>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Arial">Arial</option>
                        <option value="Comic Sans MS">Comic Sans MS</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Impact">Impact</option>
                        <option value="Verdana">Verdana</option>
                    </select>
                    {/* Font size selection */}
                    <label style={styles.label}>
                        Font Size:
                        <input
                            type="number"
                            value={fontSize}
                            onChange={(e) => setFontSize(parseInt(e.target.value))}
                            style={styles.labelInput}
                        />
                    </label>
                    {/* Color selection */}
                    <label style={styles.label}>
                        Color:
                    <input 
                        type="color"
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                        style={styles.labelInput}
                    />
                    </label>
                    {/* Font weight toggle */}
                    <label style={styles.label}>
                        Bold:
                        <input
                            type="checkbox"
                            checked={isBold}
                            onChange={(e) => setIsBold(e.target.checked)}
                            style={styles.labelInput}
                        />
                    </label>
                    {/* Font style toggle */}
                    <label style={styles.label}>
                        Italic:
                        <input
                            type="checkbox"
                            checked={isItalic}
                            onChange={(e) => setIsItalic(e.target.checked)}
                            style={styles.labelInput}
                        />
                    </label>
                    {/* Underline toggle */}
                    <label style={styles.label}>
                        Underline:
                        <input
                            type="checkbox"
                            checked={isUnderline}
                            onChange={(e) => setIsUnderline(e.target.checked)}
                            style={styles.labelInput}
                        />
                    </label>
                {/* Add Text button to trigger text mode */}
                <button style={styles.drawBtn}
                    onClick={addText}
                > Add Text
                </button>
                </div>
            </div>
        </div>
    );
};

// styles to design UI
const styles = {
    container: {
        width: CANVAS_WIDTH * DISPLAY_SCALE + 40,
        maxWidth: CONTAINER_WIDTH,
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        background: '#f1a64a',
        color: 'black',
        padding: '0px 10px',
        borderRadius: '12px 12px 0 0',
        textAlign: 'left',
        fontSize: '18px',
        gap: '2px'
    },
    controlsBar: {
        display: 'flex',
        justifyContent: 'center',
        gap: '90px',
        alignItems: 'center',
        flexWrap: 'wrap',
        padding: '6px',
        backgroundColor: '#f5f5f5',
        borderRadius: '0 0 12px 12px'
    },
    fileBtn: {
        cursor: 'pointer',
        padding: '8px 12px',
        backgroundColor: '#919291',
        color: 'white',
        fontSize: '20px',
        fontWeight: '500'
    },
    canvasWrapper:{
        padding: '20px',
        height: CANVAS_HEIGHT * DISPLAY_SCALE,
        // width: CANVAS_WIDTH * DISPLAY_SCALE,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#e0e0e0',
        borderRadius: '12px',
        border: '1px solid',
        overflow: 'hidden'
    },
    toolBar: {
        padding: '10px',
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    drawBtn: {
        padding: '12px 12px', 
        backgroundColor: '#919291',
        color: 'white',
        fontSize: '18px',
        fontWeight: '500',
    },
    drawOptions: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexDirection: 'column',
        padding: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        borderColor: '#919291',
        borderWidth: '1px',
        borderStyle: 'solid'
    },
    select: {
        padding: '6px',
        fontSize: '16px'
    },
    label: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
        fontSize: '16px',
        cursor: 'pointer'
    },
    textOptions:{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexDirection: 'row',
        padding: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px',
        borderColor: '#919291',
        borderWidth: '1px',
        borderStyle: 'solid'
    },
    labelInput: {
        width: '60px',
        height: '30px',
        marginLeft: '6px'
    },
};

export default MainPage;